import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { createGlobalStyle } from 'styled-components';
import ProgramSectionView from './ProgramSectionView';
import { CoverPrintPages } from './ProgramPages';
import { PRINT_PAGE_MM } from './programModel';

// The program as a PDF, on a static copy of the public program (ProgramSectionView in print mode)
// rendered into its own root at the end of <body>. While it prints, everything else on the page is
// hidden, so the PDF holds the program alone, on A4, without the page, buttons, cards or animations
// around it. Two printers use it: the admin's browser (ProgramPrint, "Save as PDF" in the print dialog)
// and the server's Chromium, which opens the print page (pages/ProgramPrintPage.js) and waits for
// window.programPrintReady.

const ROOT_ID = 'program-print-root';
// The printed area of a page: A4 less the @page margins below (its height, PRINT_PAGE_MM, lives in
// programModel, where ProgramSectionView reads it to place the PDF's flowers page by page).
const PRINT_WIDTH_MM = 186;
const PX_PER_MM = 96 / 25.4;

const PrintStyles = createGlobalStyle`
  @media screen {
    #${ROOT_ID} { display: none; }
  }

  @media print {
    /* the website's light blue on the whole sheet, margins included (Chromium browsers; others
       print white margins around it) */
    @page { size: A4; margin: 14mm 12mm 16mm; background: #d6e8f4; }

    html, body.program-printing { background: transparent !important; }
    body.program-printing > *:not(#${ROOT_ID}) { display: none !important; }

    #${ROOT_ID} {
      width: ${PRINT_WIDTH_MM}mm; /* A4's 210mm less the @page margins, also where the layout is only emulated */
      font-family: Roboto, Arial, sans-serif;
      font-size: 12pt; /* the customers' normal text size (body 12pt, talks 12.5pt) */
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
`;

// Waits until every image of the print copy has loaded (or failed), so the PDF has the photos; a slow
// image server holds the print dialog back for IMAGE_WAIT_MS at most.
const IMAGE_WAIT_MS = 4000;
const imagesSettled = (root) => Promise.race([
  Promise.all([...root.querySelectorAll('img')].map((img) => (
    img.complete ? Promise.resolve() : new Promise((resolve) => {
      img.addEventListener('load', resolve, { once: true });
      img.addEventListener('error', resolve, { once: true });
    })
  ))),
  new Promise((resolve) => { setTimeout(resolve, IMAGE_WAIT_MS); }),
]);

// How many pages the program fills before the sponsor pages and after them, from the print layout of
// `root` (ProgramSectionView's printPages and printPagesAfterSponsors). The layout is one flow
// (emulated print media cuts nothing), so this can only count too few pages, never too many: a block
// that does not fit at the end of a page moves to the next one when printed. A sliver of under a
// millimetre (rounding) counts as no page.
const pageCounts = (root) => {
  const mm = (px) => px / PX_PER_MM;
  const pagesOf = (px) => Math.max(0, Math.ceil((mm(px) - 1) / PRINT_PAGE_MM));
  const box = root.getBoundingClientRect();
  const sponsors = root.querySelector('[data-print-break-pages]')?.getBoundingClientRect();
  if (!sponsors) return { before: pagesOf(box.height), after: 0 };
  return { before: pagesOf(sponsors.top - box.top), after: pagesOf(box.bottom - sponsors.bottom) };
};

// The print copy of the program at the end of <body>: rendered, its images awaited, then the page is
// switched to print it alone (body.program-printing) and onReady is called. Used by ProgramPrint (the
// admin's print dialog) and by the print page the server's PDF renderer opens (ProgramPrintPage).
// Where the print layout is already applied before printing - the server's renderer emulates print
// media - the copy measures the pages it fills and only then gets the flowers of those pages
// (ProgramSectionView's printPages; flowers for pages it does not fill would add empty pages), whose
// images are awaited too. In the admin's own print dialog the layout is not known beforehand, so that
// PDF has the lung flower only. withCover puts the title pages of the PDF in front (the admin's print;
// the server puts them in front of the PDF it renders itself).
export const ProgramPrintCopy = ({ program, people, language, title, onReady, withCover = false }) => {
  const [root, setRoot] = useState(null);
  const [printPages, setPrintPages] = useState(null);

  useEffect(() => {
    const el = document.createElement('div');
    el.id = ROOT_ID;
    document.body.appendChild(el);
    setRoot(el);
    return () => {
      document.body.classList.remove('program-printing');
      el.remove();
    };
  }, []);

  useEffect(() => {
    if (!root) return undefined;
    let cancelled = false;
    imagesSettled(root).then(() => {
      if (cancelled) return;
      const printLayout = window.matchMedia?.('print').matches;
      setPrintPages(printLayout && root.getBoundingClientRect().height > 0 ? pageCounts(root) : { before: 0, after: 0 });
    });
    return () => { cancelled = true; };
  }, [root]);

  useEffect(() => {
    if (!root || printPages == null) return undefined;
    let cancelled = false;
    // only the flowers just added are new images; without them everything is in already
    (printPages.before + printPages.after > 0 ? imagesSettled(root) : Promise.resolve()).then(() => {
      if (cancelled) return;
      document.body.classList.add('program-printing');
      onReady();
    });
    return () => { cancelled = true; };
  }, [root, printPages, onReady]);

  if (!root) return null;
  return createPortal(
    <>
      <PrintStyles />
      {withCover && <CoverPrintPages program={program} alt={title || ''} />}
      <ProgramSectionView program={program} people={people} language={language} title={title} print
        printPages={printPages?.before || 0} printPagesAfterSponsors={printPages?.after || 0} />
    </>,
    root,
  );
};

// Mounted while the admin's print runs: the print copy with the title pages in front, the browser's
// print dialog once it is ready, and onDone when the dialog closes.
const ProgramPrint = ({ program, people, language, title, onDone }) => {
  useEffect(() => {
    window.addEventListener('afterprint', onDone, { once: true });
    return () => window.removeEventListener('afterprint', onDone);
  }, [onDone]);

  // Browsers that print synchronously have already fired afterprint when print() returns.
  const print = useCallback(() => window.print(), []);

  return <ProgramPrintCopy program={program} people={people} language={language} title={title} onReady={print} withCover />;
};

export default ProgramPrint;
